import { Icon, Skeleton } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import { SxProps } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import ModalEliminarUsuario from '@/components/Modals/TablaUsuario/ModalEliminarUsuario';
import ModalVerUsuarioV2 from '@/components/Modals/TablaUsuario/ModalVerUsuarioV2';
import dayjs from 'dayjs';
import { ChangeEvent, MouseEvent, useMemo, useState } from 'react';
import { IUser } from '@/redux/models';
import { IEmpresa } from '@/redux/models/empresaTypes';
import { useAppSelector } from '@/redux/store';
import { Order, getComparator, stableSort } from '@/utils/tableUtils';
import './TablaUsuariosV2.css';
import ChipRole from './components/ChipRole';
import CompanyItem from './components/CompanyItem/CompanyItem';
import EnhancedTableToolbar from './components/EnhancedTableToolbar/EnhancedTableToolbar';
import LoadingRows from './components/LoadingRows/LoadingRows';
import NameItem from './components/NameItem/NameItem';
import DeleteUser from './components/UserActions/DeleteUser';
import { TablaUsuariosHookReturnType } from './useTablaUsuarios';


interface HeadCell {
    disablePadding: boolean;
    id: keyof IUser | any;
    label: string;
    align?: 'right' | 'left' | 'center';
    sx?: SxProps<any>
}

const headCells: readonly HeadCell[] = [
    {
        id: 'nombre',
        disablePadding: true,
        label: 'Nombre',
    },
    {
        id: 'empresa',
        disablePadding: false,
        label: 'Empresa',
        sx: { color: '#637381' }
    },
    {
        id: 'telefono',
        disablePadding: false,
        label: 'Teléfono',
        sx: { color: '#637381' }
    },
    {
        id: 'cuit',
        disablePadding: false,
        label: 'Cuit',
        sx: { color: '#637381' }

    },
    {
        id: 'createdAt',
        disablePadding: false,
        label: 'Creado',
        sx: { color: '#637381' }
    },
    {
        id: 'role',
        disablePadding: true,
        label: 'Rol',
        sx: { width: '5rem', textAlign: 'center', color: '#637381' }
    },
];

interface EnhancedTableProps {
    numSelected: number;
    onRequestSort: (event: MouseEvent<unknown>, property: keyof IUser) => void;
    onSelectAllClick: (event: ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy: string;
    rowCount: number;
    loading: boolean;
    hideColumnEmpresa: boolean;
}

function EnhancedTableHead(props: EnhancedTableProps) {
    const { order, orderBy, numSelected, rowCount, onRequestSort, loading, hideColumnEmpresa } = props;
    const createSortHandler = (property: keyof IUser) => (event: MouseEvent<unknown>) => {
        onRequestSort(event, property);
    };

    const fixedCells = useMemo(() => {
        return headCells.filter((cell) => hideColumnEmpresa ? cell.id !== 'empresa' : true);
    }, [hideColumnEmpresa, headCells]);

    const renderSkeletons = () => {
        return (
            <TableRow>
                <TableCell padding="checkbox">
                </TableCell>
                {fixedCells.map((headCell) => (
                    <TableCell key={headCell.id}>
                        <Skeleton variant="text" width={100} />
                    </TableCell>
                ))}
                <TableCell>
                </TableCell>
            </TableRow>
        );
    };

    return (
        <TableHead>
            {loading ? (
                renderSkeletons() // Mostrar Skeletons si loading es true
            ) : (
                <TableRow>
                    <TableCell padding="checkbox">
                    </TableCell>
                    {fixedCells.map((headCell) => (
                        <TableCell
                            key={headCell.id}
                            align={headCell.align ?? 'left'}
                            padding={headCell.disablePadding ? 'none' : 'normal'}
                            sortDirection={orderBy === headCell.id ? order : false}
                            sx={headCell.sx}
                        >
                            <TableSortLabel
                                active={orderBy === headCell.id}
                                direction={orderBy === headCell.id ? order : 'asc'}
                                onClick={createSortHandler(headCell.id)}
                            >
                                {headCell.label}
                                {orderBy === headCell.id ? (
                                    <Box component="span" sx={visuallyHidden}>
                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                    </Box>
                                ) : null}
                            </TableSortLabel>
                        </TableCell>
                    ))}
                    <TableCell >
                    </TableCell>
                </TableRow>
            )}
        </TableHead>
    );
}

interface TablaUsuariosProps {
    hook: TablaUsuariosHookReturnType;
    hideSearchCompany?: boolean;
    hideColumnEmpresa?: boolean;
}


export default function TablaUsuariosV2({ hook, hideSearchCompany = false, hideColumnEmpresa = false }: TablaUsuariosProps) {
    const user = useAppSelector((state: any) => state.userState.user);

    const { usuarios, isLoading, empresas, editarUsuario, eliminarUsuario } = hook;

    const [order, setOrder] = useState<Order>('desc');
    const [orderBy, setOrderBy] = useState<keyof IUser>('createdAt');
    const [selected, setSelected] = useState<readonly number[]>([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [searchValue, setSearchValue] = useState<string>('');

    const [modalAbierto, setModalAbierto] = useState<'borrar' | 'editar' | null>(null);
    const [userSelected, setUserSelected] = useState<IUser | null>(null);
    const [empresaSelected, setEmpresaSelected] = useState<IEmpresa | null>(null);

    const handleRequestSort = (
        event: MouseEvent<unknown>,
        property: keyof IUser,
    ) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event: ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = usuarios.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };


    const handleChangePage = (event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const isSelected = (id: number) => selected.indexOf(id) !== -1;

    const filteredUsers = useMemo(
        () =>
            usuarios.filter((user) =>
                user.nombre.toLowerCase().includes(searchValue.toLowerCase()) ||
                user.apellido.toLowerCase().includes(searchValue.toLowerCase()) ||
                user.email.toLowerCase().includes(searchValue.toLowerCase()) ||
                user.company?.nombre.toLowerCase().includes(searchValue.toLowerCase())
            ).filter((user) => empresaSelected ? user.company?.id === empresaSelected.id : true),
        [searchValue, isLoading, empresaSelected, usuarios]
    );

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - usuarios.length) : 0;

    const visibleRows = useMemo(
        () =>
            stableSort(
                filteredUsers as any,
                getComparator(order, orderBy)
            ).slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredUsers, order, orderBy, page, rowsPerPage, isLoading, usuarios]
    );



    return (<>
        <Box sx={{ width: '100%' }} id="tablaUsuariosV2">
            <Paper sx={{ width: '100%', mb: 2 }}>
                <EnhancedTableToolbar
                    hideSearchCompany={hideSearchCompany}
                    numSelected={selected.length} loading={isLoading}
                    empresas={empresas} empresaSelected={empresaSelected} setEmpresaSelected={setEmpresaSelected}
                    searchValue={searchValue} onChangeSearchValue={setSearchValue}
                />
                <TableContainer className={isLoading ? 'fade-out' : 'fade-in'}>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size='medium'
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={usuarios.length}
                            loading={isLoading}
                            hideColumnEmpresa={hideColumnEmpresa}
                        />
                        <TableBody>
                            {isLoading ? <LoadingRows /> : visibleRows.map((row, index) => {
                                const isItemSelected = isSelected(Number(row.id));
                                const labelId = `enhanced-table-checkbox-${index}`;

                                return (
                                    <TableRow
                                        hover
                                        aria-checked={isItemSelected}
                                        tabIndex={-1}
                                        key={row.id}
                                        selected={isItemSelected}
                                    >
                                        <TableCell padding="checkbox" sx={{ textAlign: 'center', color: 'gray' }}>
                                            {user.role === 'dev' && `#${row.id}`}
                                        </TableCell>
                                        <TableCell
                                            component="th"
                                            id={labelId}
                                            scope="row"
                                            padding="none"
                                        >
                                            <NameItem user={row} />
                                        </TableCell>
                                        {!hideColumnEmpresa && <TableCell align="left">
                                            <CompanyItem user={row} />
                                        </TableCell>}
                                        <TableCell align="left">{row.telefono}</TableCell>
                                        <TableCell align="left">{row.cuit}</TableCell>
                                        <TableCell align="left">{dayjs(row.createdAt).format('DD/MM/YYYY')}</TableCell>
                                        <TableCell align="left"  >
                                            <ChipRole label={row.role as IUser["role"]} />
                                        </TableCell>
                                        <TableCell align="left" sx={{ display: 'table-cell' }}>
                                            <Box display='flex' justifyContent='center'>

                                                <IconButton onClick={() => {
                                                    setUserSelected(row)
                                                    setModalAbierto('editar')
                                                }}>
                                                    <Icon>edit</Icon>
                                                </IconButton>
                                                <DeleteUser
                                                    onClick={() => {
                                                        setUserSelected(row)
                                                        setModalAbierto('borrar')
                                                    }} />
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {emptyRows > 0 && (
                                <TableRow
                                    style={{
                                        height: 73 * emptyRows,
                                    }}
                                >
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={usuarios.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

        </Box>
        <ModalVerUsuarioV2 editarUsuario={editarUsuario} empresas={empresas} open={userSelected != null && modalAbierto === 'editar'} user={userSelected || {} as IUser} onClose={() => { setUserSelected(null); setModalAbierto(null) }} />
        <ModalEliminarUsuario eliminarUsuario={eliminarUsuario} open={userSelected != null && modalAbierto === 'borrar'} user={userSelected || {} as IUser} onClose={() => { setUserSelected(null); setModalAbierto(null) }} />
    </>
    );
}